import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RootState, useAppDispatch, useAppSelector } from '../../../state/state';
import { closeModal } from '../../../state/ModalSlice/ModalSlice';
import { TextFieldComponent, DropdownComponent, RadioComponent, CheckboxComponent } from '../customFieldsFunctions';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../Config/firebase';
import { toast } from 'react-toastify';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';

const EditCustomerKYCFieldModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((state: RootState) => state.ModalSlice.EditCustomerKYCFieldModalOpen);
  const serviceProviderId = useAppSelector(selectEffectiveUID); 
  const { question } = useAppSelector((state: RootState) => state.ArbitrarySlice.ForEditCustomerKYCFieldModal);
  
  const [fieldData, setFieldData] = useState<any>({
    fieldDescription: "",
    isRequired: false,
    type: "",
    options: []
  });

  useEffect(() => {
    if (question) {
      setFieldData({
        fieldDescription: question.fieldDescription,
        isRequired: question.isRequired,
        type: question.type,
        options: question?.options || []
      });
    }
  }, [question]);

  const handleClose = () => {
    dispatch(closeModal("EditCustomerKYCFieldModalOpen"));
  };

  const handleFieldChange = (newData: { fieldDescription: string; isRequired: boolean; options?: string[] }) => {
    setFieldData({
      fieldDescription: newData.fieldDescription,
      isRequired: newData.isRequired,
      type: question.type,
      options: newData.options || [],
      id: question.id
    });
  };

  const handleSubmit = async (updatedField: { 
    fieldDescription: string; 
    isRequired: boolean; 
    type: string; 
    options?: string[] 
  }) => {
    try {
      const serviceProviderRef = doc(db, 'ServiceProviders', serviceProviderId);
      
      // Fetch current document
      const serviceProviderDoc = await getDoc(serviceProviderRef);
      if (!serviceProviderDoc.exists()) {
        throw new Error('Service provider not found');
      }

      const currentFields = serviceProviderDoc.data().customerKYCFields || [];
      const updatedFields = currentFields.map((f: any) => 
        f.id === question.id ? { ...updatedField, id: question.id } : f
      );

      // Update with new array
      await updateDoc(serviceProviderRef, {
        customerKYCFields: updatedFields
      });

      toast.success('KYC field updated successfully');
      handleClose();
    } catch (error) {
      console.error('Error updating custom field:', error);
      toast.error('Error updating field');
    }
  };

  const renderFieldComponent = () => {
    if (!fieldData.type) return null;

    const props = {
      fieldDescription: fieldData.fieldDescription,
      isRequired: fieldData.isRequired,
      existingOptions: fieldData.options,
      onFieldChange: handleFieldChange,
      onSubmit: handleSubmit,
      initialOptions: fieldData.options,
      optional: true
    };

    switch (fieldData.type) {
      case 'textfield':
        return <TextFieldComponent {...props}  />;
      case 'dropdown':
        return <DropdownComponent {...props} />;
      case 'radio':
        return <RadioComponent {...props} />;
      case 'checkbox':
        return <CheckboxComponent {...props} />;
      default:
        return null;
    }
  };

  return (
    <Dialog
      sx={{
        padding: 0,
        '& .MuiPaper-root': {
          borderRadius: '10px',
        },
      }}
      maxWidth="md"
      open={isOpen}
      PaperProps={{
        component: 'div',
        className: 'relative',
      }}
    >
      <DialogContent sx={{ paddingX: "60px", paddingY: "50px" }} className="bg-white">
        <div className="w-[550px]">
          <div className="header pb-[15px] space-y-2">
            <h2 className="text-2xl font-semibold text-gray-800 transition-all duration-300">
              Edit Customer KYC Field
            </h2>
            <p className="text-gray-500 font-medium">
              Update your custom KYC field details
            </p>
          </div>

          <div className="absolute right-4 top-4">
            <IconButton 
              onClick={handleClose}
              className="hover:rotate-90 transition-transform duration-300"
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.04)',
                }
              }}
            >
              <CloseIcon className="text-gray-500 hover:text-gray-700" />
            </IconButton>
          </div>

          <Box className="min-h-[200px]">
            {renderFieldComponent()}
          </Box>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditCustomerKYCFieldModal;
